"use client";
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";

// Form field definitions per type
const FORM_FIELDS_BY_TYPE = {
  surface: [
    { name: "surface", label: "Surface", type: "text", required: true },
    { name: "value",   label: "Value",   type: "text", required: true },
    { name: "name",    label: "Name",    type: "text", required: true },
    { name: "unit",    label: "Unit",    type: "text", required: false },
  ],
  consumption: [
    { name: "consumption",     label: "Consumption",     type: "text", required: true },
    { name: "value",           label: "Value",           type: "text", required: true },
    { name: "name",            label: "Name",            type: "text", required: true },
    { name: "unit",            label: "Unit",            type: "text", required: false },
    // Client 2026-08-31 (Confédération Centre): Energie was never imported, so it
    // was invisible here and the Compteurs page guessed it from the meter name.
    // Now a real imported field, editable like the rest.
    { name: "energie",         label: "Énergie",         type: "text", required: false },
    // Phase 3 (Track B): optional meter-identification fields. Empty cells stay NULL.
    { name: "compteur",        label: "Compteur (meter serial)", type: "text", required: false },
    { name: "current_reading", label: "Current Reading (period end)", type: "text", required: false },
  ],
  cost: [
    { name: "cost",       label: "Cost",       type: "text", required: true },
    { name: "value",      label: "Value",      type: "text", required: true },
    { name: "value_vat",  label: "Value VAT",  type: "text", required: true },
    { name: "name",       label: "Name",       type: "text", required: true },
    { name: "type",       label: "Type",       type: "text", required: false },
    { name: "connection", label: "Connection", type: "text", required: false },
  ],
  // Phase 3 (A10): invoice-level cost detail. Form names match tbl_cost_invoice
  // columns + the backend's allowedFieldsByType.cost_invoice entry. Required
  // fields mirror the import flow: cost_number, invoice_date, amount_ht,
  // amount_ttc.
  cost_invoice: [
    { name: "cost_number",   label: "Cost",        type: "text", required: true },
    { name: "invoice_date",  label: "Date",        type: "date", required: true },
    { name: "libelle",       label: "Label",       type: "text", required: false },
    { name: "quantity",      label: "Quantity",    type: "text", required: false },
    { name: "price_ht",      label: "Price HT",    type: "text", required: false },
    { name: "amount_ht",     label: "Amount HT",   type: "text", required: true  },
    { name: "tva_pct",       label: "VAT %",       type: "text", required: false },
    { name: "tva",           label: "VAT",         type: "text", required: false },
    { name: "amount_ttc",    label: "Amount TTC",  type: "text", required: true  },
    // M1.3: Nr facture from the standardised Coûts report layout. Optional.
    { name: "invoice_number", label: "Invoice Number (Nr facture)", type: "text", required: false },
  ],
  object: [
    { name: "object",               label: "Object",                       type: "text", required: true },
    { name: "object_surface",       label: "Object Surface",               type: "text", required: true },
    { name: "object_surface_value", label: "Object Surface Value",         type: "text", required: true },
    { name: "start_date",           label: "Start Date (FEE)",             type: "date", required: true },
    { name: "end_date",             label: "End Date (FEE)",               type: "date", required: false },
    // Phase 3 (Track B): physical-unit code + original rental start. Both optional.
    { name: "objet_code",           label: "Objet (unit code, e.g. B-00-01)", type: "text", required: false },
    { name: "loc_start_date",       label: "Loc. Start Date (rental start)",  type: "date", required: false },
    // M1.3: Genre d'objet (e.g. "surface bureau", "Dépôt principal").
    { name: "genre_objet",          label: "Genre d'objet",                type: "text", required: false },
    // M1.5: Floor label (e.g. "rez-de-chaussée", "1er étage").
    { name: "floor",                label: "Floor (Étage)",                type: "text", required: false },
  ],
  etc: [
    { name: "start_date", label: "Start Date", type: "date", required: true },
    { name: "end_date",   label: "End Date",   type: "date", required: false },
    { name: "value",      label: "Value",      type: "text", required: true },
  ],
  djf: [
    { name: "start_date", label: "Start Date", type: "date", required: true },
    { name: "end_date",   label: "End Date",   type: "date", required: false },
    { name: "value",      label: "Value",      type: "text", required: true },
  ],
  nj: [
    { name: "start_date", label: "Start Date", type: "date", required: true },
    { name: "end_date",   label: "End Date",   type: "date", required: false },
    { name: "value",      label: "Value",      type: "text", required: true },
  ],
};

const authHeaders = () => ({
  "api-key": process.env.REACT_APP_API_KEY,
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const ImportedDataList = () => {
  const { type } = useParams();
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [availableBuildings, setAvailableBuildings] = useState([]);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [savingCell, setSavingCell] = useState(false);
  const savingRef = useRef(false);

  // Delete state
  const [deletingId, setDeletingId] = useState(null);

  // Add record modal state
  const [addModal, setAddModal] = useState(null); // { buildingId, periodId, buildingName, from, to }
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [formSaving, setFormSaving] = useState(false);

  const typeNames = {
    surface:      "Surface Data",
    cost:         "Cost Data",
    cost_invoice: "Cost Invoices",
    consumption:  "Energy Consumption",
    object:       "Object Data",
    etc:          "ETC Data",
    djf:          "DJF Data",
    nj:           "NJ Data",
  };

  const nonEditableKeys = [
    "id", "building_id", "building_time_period_id",
    "created_at", "updated_at", "deleted_at", "building", "time_period",
    // Phase 3 (B0): the API now returns the joined tenant relation on object rows.
    // We keep `tenant_id` / `tenant` out of the displayed columns and surface the
    // tenant name as a derived display-only `locataire` cell instead.
    "tenant", "tenant_id",
  ];

  // Phase 3 (B0): flatten the joined tenant relation into a display-only `locataire`
  // field on each object record. Called once after fetch so the table iteration
  // naturally renders it as a column without any further wiring. Other types are
  // untouched.
  const decorateBuildingsWithLocataire = (buildingsList) => {
    if (String(type).toLowerCase() !== 'object') return buildingsList;
    return (buildingsList || []).map((b) => ({
      ...b,
      periods: (b.periods || []).map((p) => ({
        ...p,
        records: (p.records || []).map((r) => ({
          ...r,
          locataire: r.tenant && r.tenant.name ? r.tenant.name : '—',
        })),
      })),
    }));
  };

  // Phase 3: date fields across all types. Inline edit renders these as
  // <input type="date"> and serialises through toISODateStringOrNull.
  //   start_date / end_date  → object/etc/djf/nj/surface (existing)
  //   invoice_date           → cost_invoice (A10)
  //   loc_start_date         → object (Track B — original rental start)
  const DATE_FIELDS    = new Set(["start_date", "end_date", "invoice_date", "loc_start_date"]);
  // Fields whose empty cells should fall back to NULL instead of empty string.
  const NULLABLE_FIELDS = new Set([
    "unit", "type", "connection", "end_date",
    "libelle", "quantity", "price_ht", "tva_pct", "tva", "full_surface_value",
    "objet_code", "loc_start_date", "compteur", "current_reading",
    // M1.3: new optional columns introduced by the standardised report layout
    "genre_objet", "invoice_number",
    // M1.5: floor label on objects (optional, overrides derived Étage).
    "floor",
  ]);

  // ─── helpers ───────────────────────────────────────────────────────────────

  const toYYYYMMDDInputValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
      if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return value.slice(0, 10);
    }
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const toISODateStringOrNull = (value) => {
    if (!value) return null;
    if (typeof value !== "string") return value;
    const d = new Date(`${value}T00:00:00.000Z`);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  const patchRecordValueInState = (state, recordId, field, newValue) =>
    state.map((b) => ({
      ...b,
      periods: (b?.periods || []).map((p) => ({
        ...p,
        records: (p?.records || []).map((r) =>
          r?.id !== recordId ? r : { ...r, [field]: newValue }
        ),
      })),
    }));

  const appendRecordToState = (state, periodId, newRecord) =>
    state.map((b) => ({
      ...b,
      periods: (b?.periods || []).map((p) =>
        p.building_time_period_id !== periodId
          ? p
          : { ...p, records: [...(p.records || []), newRecord] }
      ),
    }));

  const removeRecordFromState = (state, recordId) =>
    state.map((b) => ({
      ...b,
      periods: (b?.periods || []).map((p) => ({
        ...p,
        records: (p?.records || []).filter((r) => r?.id !== recordId),
      })),
    }));

  const formatValue = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-GB");
    }
    if (value instanceof Date) return value.toLocaleDateString("en-GB");
    if (typeof value === "object") {
      try { return JSON.stringify(value); } catch { return "[Object]"; }
    }
    return String(value);
  };

  // ─── inline edit ───────────────────────────────────────────────────────────

  const startEditingCell = (record, field) => {
    if (!record?.id || savingRef.current) return;
    // Phase 3 (B0): block edit attempts on fields not declared editable by the
    // type's FORM_FIELDS. Without this, a double-click on a non-editable cell
    // would fire a PUT that the backend rejects.
    // Phase 3 (B0 follow-up): `locataire` is derived from `tenant.name` but is
    // editable through a special backend code path that upserts the tenant.
    // Allow it explicitly for the object type.
    const editableFields = new Set((FORM_FIELDS_BY_TYPE[type] || []).map((f) => f.name));
    const isEditableLocataire = type === "object" && field === "locataire";
    if (!editableFields.has(field) && !isEditableLocataire) return;
    const original = record[field];
    const originalDraft = DATE_FIELDS.has(field)
      ? toYYYYMMDDInputValue(original)
      // The cell displays the em-dash for "no tenant"; pre-fill an empty input
      // instead so the user types in clean space.
      : (isEditableLocataire && (original === "—" || original == null) ? "" : original ?? "");
    setEditingCell({ recordId: record.id, field, originalDraft });
    setDraftValue(String(originalDraft));
  };

  const cancelEditing = () => { setEditingCell(null); setDraftValue(""); };

  const saveEditingCell = async () => {
    if (!editingCell || savingRef.current) return;
    if (draftValue === editingCell.originalDraft) { cancelEditing(); return; }

    const { recordId, field } = editingCell;
    savingRef.current = true;
    setSavingCell(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}imported-data/${type}/${recordId}`,
        { field, value: draftValue },
        { headers: { ...authHeaders(), "Content-Type": "application/json" } }
      );

      const updated = res?.data?.data?.updated;

      // Phase 3 (B0 follow-up): locataire edit. Backend returned the object with
      // `tenant` populated; reflect both the display field (`locataire`) and the
      // underlying relation (`tenant` / `tenant_id`) in state so the cell shows
      // the new value and any future render path stays consistent.
      if (field === "locataire" && updated) {
        const newLocataire = updated.tenant?.name || "—";
        setBuildings((prev) => {
          let next = patchRecordValueInState(prev, recordId, "locataire", newLocataire);
          next = patchRecordValueInState(next, recordId, "tenant", updated.tenant || null);
          next = patchRecordValueInState(next, recordId, "tenant_id", updated.tenant_id ?? null);
          return next;
        });
        setFilteredData((prev) => {
          let next = patchRecordValueInState(prev, recordId, "locataire", newLocataire);
          next = patchRecordValueInState(next, recordId, "tenant", updated.tenant || null);
          next = patchRecordValueInState(next, recordId, "tenant_id", updated.tenant_id ?? null);
          return next;
        });
        cancelEditing();
        return;
      }

      const apiValue = updated && Object.hasOwn(updated, field) ? updated[field] : undefined;
      const localNewValue = (() => {
        if (apiValue !== undefined) return apiValue;
        if (DATE_FIELDS.has(field)) return toISODateStringOrNull(draftValue);
        if (NULLABLE_FIELDS.has(field) && draftValue === "") return null;
        return draftValue;
      })();

      setBuildings((prev) => patchRecordValueInState(prev, recordId, field, localNewValue));
      setFilteredData((prev) => patchRecordValueInState(prev, recordId, field, localNewValue));
      cancelEditing();
    } catch (err) {
      console.error("Error updating imported data:", err);
      window.alert(err?.response?.data?.message || err?.message || "Failed to update. Please try again.");
    } finally {
      savingRef.current = false;
      setSavingCell(false);
    }
  };

  // ─── delete ────────────────────────────────────────────────────────────────

  const handleDeleteClick = (e) => deleteRecord(Number(e.currentTarget.dataset.recordId));

  const deleteRecord = async (recordId) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    setDeletingId(recordId);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}imported-data/${type}/${recordId}`,
        { headers: authHeaders() }
      );
      setBuildings((prev) => removeRecordFromState(prev, recordId));
      setFilteredData((prev) => removeRecordFromState(prev, recordId));
    } catch (err) {
      console.error("Error deleting imported data:", err);
      window.alert(err?.response?.data?.message || err?.message || "Failed to delete. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // ─── add record modal ──────────────────────────────────────────────────────

  const openAddModal = (e) => {
    const buildingId   = Number(e.currentTarget.dataset.buildingId);
    const periodId     = Number(e.currentTarget.dataset.periodId);
    const buildingName = e.currentTarget.dataset.buildingName;
    const from         = e.currentTarget.dataset.from;
    const to           = e.currentTarget.dataset.to;
    const initial = {};
    (FORM_FIELDS_BY_TYPE[type] || []).forEach((f) => { initial[f.name] = ""; });
    setFormData(initial);
    setFormErrors({});
    setAddModal({ buildingId, periodId, buildingName, from, to });
  };

  const closeAddModal = () => { if (!formSaving) setAddModal(null); };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    (FORM_FIELDS_BY_TYPE[type] || []).forEach((f) => {
      if (f.required && !formData[f.name]) errors[f.name] = `${f.label} is required`;
    });
    return errors;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setFormSaving(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}imported-data/${type}`,
        { building_id: addModal.buildingId, building_time_period_id: addModal.periodId, ...formData },
        { headers: { ...authHeaders(), "Content-Type": "application/json" } }
      );
      const newRecord = res?.data?.data?.created;
      if (newRecord) {
        // Phase 3 (B0): inject the derived `locataire` field on object rows so the new
        // record has the same shape as decorated list rows. Otherwise the table header
        // (built from records[0] keys) and this row would render a different column count
        // and visually misalign.
        const decoratedNewRecord = String(type).toLowerCase() === 'object'
          ? { ...newRecord, locataire: newRecord.tenant && newRecord.tenant.name ? newRecord.tenant.name : '—' }
          : newRecord;
        const periodId = addModal.periodId;
        setBuildings((prev) => appendRecordToState(prev, periodId, decoratedNewRecord));
        setFilteredData((prev) => appendRecordToState(prev, periodId, decoratedNewRecord));
      }
      setAddModal(null);
    } catch (err) {
      console.error("Error creating record:", err);
      window.alert(err?.response?.data?.message || err?.message || "Failed to create record. Please try again.");
    } finally {
      setFormSaving(false);
    }
  };

  // ─── filter / fetch ────────────────────────────────────────────────────────

  const fetchAvailableOptions = async () => {
    try {
      // Use a large pageSize so all buildings are included regardless of record count.
      // Without this, high-volume types (e.g. Object with 2000+ records) would cut off
      // buildings with higher IDs before they appear in the dropdown.
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}imported-data/${type}`,
        { headers: authHeaders(), params: { pageSize: 100000 } }
      );
      if (res?.data?.status === "success") {
        const data = decorateBuildingsWithLocataire(res.data.data.buildings || []);
        setAvailableBuildings(data.map((b) => ({ value: b.building_id, label: b.building_name })));
        setBuildings(data);
      }
    } catch (err) {
      console.error("Error fetching available options:", err);
    }
  };

  const fetchFilteredData = async () => {
    if (!selectedBuilding) { alert("Please select a building first"); return; }
    try {
      setLoading(true);
      setHasSearched(true);
      // Always fetch fresh from the API with building/period filters so we get complete
      // data regardless of what was pre-loaded (avoids the pageSize cutoff issue).
      const params = {
        building_id: selectedBuilding.value,
        pageSize: 100000,
      };
      if (selectedPeriod) params.building_time_period_id = selectedPeriod.value;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}imported-data/${type}`,
        { headers: authHeaders(), params }
      );
      if (res?.data?.status === "success") {
        setFilteredData(decorateBuildingsWithLocataire(res.data.data.buildings || []));
      } else {
        setFilteredData([]);
      }
    } catch (err) {
      console.error("Error filtering data:", err);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBuildingChange = (buildingOption) => {
    setSelectedBuilding(buildingOption);
    setSelectedPeriod(null);
    if (buildingOption) {
      const buildingData = buildings.find((b) => b.building_id === buildingOption.value);
      if (buildingData) {
        setAvailablePeriods(buildingData.periods.map((p) => ({
          value: p.building_time_period_id,
          label: `${p.from} to ${p.to}`,
        })));
      }
    } else {
      setAvailablePeriods([]);
    }
  };

  useEffect(() => {
    setSelectedBuilding(null);
    setSelectedPeriod(null);
    setAvailableBuildings([]);
    setAvailablePeriods([]);
    setFilteredData([]);
    setHasSearched(false);
    setBuildings([]);
    if (type && typeNames[type]) {
      fetchAvailableOptions();
    } else if (type) {
      console.error("Invalid type:", type);
      window.alert(`Invalid data type: ${type}`);
    }
  }, [type]);

  // ─── render ────────────────────────────────────────────────────────────────

  return (
    <div className="admin-page">
      <div className="admin-page-title d-flex align-items-center justify-content-between">
        <h1 className="page-section-title">{typeNames[type] || type}</h1>
      </div>

      {/* Filters */}
      <div className="key-card mb-4 p-lg-4 p-3">
        <h5 className="mb-3">Filters</h5>
        <div className="row">
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="building">Building</label>
              <div className="custom-select">
                <Select
                  options={availableBuildings}
                  value={selectedBuilding}
                  onChange={handleBuildingChange}
                  placeholder="Select building"
                  isClearable
                />
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label htmlFor="period">Time Period</label>
              <div className="custom-select">
                <Select
                  options={availablePeriods}
                  value={selectedPeriod}
                  onChange={setSelectedPeriod}
                  placeholder="Select time period (optional)"
                  isDisabled={!selectedBuilding}
                  isClearable
                />
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-group">
              <label>&nbsp;</label>
              <button
                type="button"
                className="btn btn-primary w-100"
                onClick={fetchFilteredData}
                disabled={!selectedBuilding}
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Data table */}
      <div className="properties-listing">
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p>Loading {typeNames[type] || type}...</p>
          </div>
        ) : !hasSearched ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p>Please select a building and click Search to view imported data.</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p>No imported data found for the selected filters.</p>
          </div>
        ) : (
          <>
            {filteredData.map((building) => (
              <div key={building.building_id} className="building-section">
                {building.periods.map((period) => (
                  <div key={period.building_time_period_id} className="period-section">
                    <div className="section-header d-flex align-items-center justify-content-between">
                      <h3 className="mb-0">
                        {building.building_name} — {period.from} to {period.to}
                      </h3>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        data-building-id={building.building_id}
                        data-period-id={period.building_time_period_id}
                        data-building-name={building.building_name}
                        data-from={period.from}
                        data-to={period.to}
                        onClick={openAddModal}
                      >
                        + Add Record
                      </button>
                    </div>

                    <div className="common-table">
                      <div className="table-wrpper">
                        <table>
                          <thead>
                            <tr>
                              {period.records.length > 0 &&
                                Object.keys(period.records[0])
                                  .filter((key) => !nonEditableKeys.includes(key))
                                  .map((key) => (
                                    <th key={key}>{key.replaceAll("_", " ").toUpperCase()}</th>
                                  ))}
                              <th>ACTIONS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {period.records.map((record) => (
                              <tr key={record.id}>
                                {Object.entries(record)
                                  .filter(([key]) => !nonEditableKeys.includes(key))
                                  .map(([key, value]) => {
                                    const isEditing =
                                      editingCell?.recordId === record.id &&
                                      editingCell?.field === key;
                                    if (isEditing) {
                                      return (
                                        <td key={key}>
                                          <input
                                            className="imported-inline-edit-input"
                                            type={DATE_FIELDS.has(key) ? "date" : "text"}
                                            value={draftValue}
                                            autoFocus
                                            onChange={(e) => setDraftValue(e.target.value)}
                                            onBlur={saveEditingCell}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter") { e.preventDefault(); saveEditingCell(); }
                                              else if (e.key === "Escape") { e.preventDefault(); cancelEditing(); }
                                            }}
                                            disabled={savingCell}
                                          />
                                        </td>
                                      );
                                    }
                                    return (
                                      <td
                                        key={key}
                                        onDoubleClick={() => startEditingCell(record, key)}
                                        style={{ cursor: "pointer" }}
                                        title="Double-click to edit"
                                      >
                                        {formatValue(value)}
                                      </td>
                                    );
                                  })}
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    data-record-id={record.id}
                                    onClick={handleDeleteClick}
                                    disabled={deletingId === record.id}
                                    title="Delete record"
                                  >
                                    {deletingId === record.id ? "Deleting…" : "Delete"}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Add Record Modal */}
      {addModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={(e) => { if (e.target === e.currentTarget) closeAddModal(); }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Add Record — {addModal.buildingName} ({addModal.from} to {addModal.to})
                </h5>
                <button type="button" className="btn-close" onClick={closeAddModal} disabled={formSaving} />
              </div>

              <form onSubmit={handleFormSubmit} noValidate>
                <div className="modal-body">
                  <div className="row">
                    {(FORM_FIELDS_BY_TYPE[type] || []).map((field) => (
                      <div key={field.name} className="col-md-6">
                        <div className="form-group mb-3">
                          <label htmlFor={`modal-field-${field.name}`}>
                            {field.label}
                            {field.required && <span className="text-danger ms-1">*</span>}
                          </label>
                          <input
                            id={`modal-field-${field.name}`}
                            name={field.name}
                            type={field.type}
                            className={`form-control${formErrors[field.name] ? " is-invalid" : ""}`}
                            value={formData[field.name] ?? ""}
                            onChange={handleFormChange}
                            placeholder={
                              field.required
                                ? `Enter ${field.label.toLowerCase()}`
                                : `Enter ${field.label.toLowerCase()} (optional)`
                            }
                          />
                          {formErrors[field.name] && (
                            <div className="invalid-feedback">{formErrors[field.name]}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeAddModal}
                    disabled={formSaving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={formSaving}>
                    {formSaving ? "Saving…" : "Save Record"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportedDataList;
